import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Users } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { TeamsApi, Team } from '@/services/users/teamsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AppShell } from '../../components/AppShell'
import { TeamTable } from '../../components/TeamTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { CardContent, CardFooter, CardIcon } from '@/components/ui/card'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'

export function TeamsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.teams.status', 'ACTIVE')
  const [selection, setSelection] = useState<Team[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const [search, setSearch] = useUrlSearch()
  const { data: teams = [], isLoading } = useQuery({
    queryKey: ['teams-all', search],
    queryFn: () => TeamsApi.getAll(search),
    staleTime: Infinity,
  })

  const filtered = statusFilter === 'all'
    ? teams
    : teams.filter((t) => t.objectState === statusFilter || (!t.objectState && statusFilter === 'ACTIVE'))

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((team) => TeamsApi.update(team.id, { name: team.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: ['teams-all'] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'teams.doneDetail_activate' : 'teams.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('teams.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('teams.title')}</h1>
            <p>{t('teams.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button onClick={() => navigate(`/admin/teams/${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}?new=true`)}>
              <Plus className="size-4" /> {t('teams.newTeam')}
            </Button>
          </div>
        </div>

        <ListCard tone="people">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Users} />
                {t('teams.title')}
              </>
            }
            count={filtered.length}
            controls={
              <>
                <SearchInput value={search} onChange={(v) => { setSearch(v); setSelection([]) }} />
                <StatusFilterButton value={statusFilter} onChange={setStatusFilter} />
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              { label: t('common.activate'), icon: 'pi pi-check-circle', severity: 'success', onClick: () => bulkSetStatus('ACTIVE') },
              { label: t('common.deactivate'), icon: 'pi pi-ban', severity: 'danger', onClick: () => bulkSetStatus('INACTIVE') },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <TeamTable
              teams={filtered}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={t('teams.noTeamsFound')}
            />
          </CardContent>

          {filtered.length > 0 && (
            <CardFooter>
              <span style={{ fontSize: 12, color: 'var(--ad-text-muted)' }}>
                {t('teams.teamCount', { count: filtered.length })}
              </span>
            </CardFooter>
          )}
        </ListCard>
      </div>
    </AppShell>
  )
}
